import { Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';

const VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => NumberInputComponent),
  multi: true,
};
const NRINPUT_CONTROL_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => NumberInputComponent),
  multi: true,
};

@Component({
  selector: 'web-components-number-input',
  templateUrl: './number-input.component.html',
  providers: [VALIDATOR, NRINPUT_CONTROL_ACCESSOR],
  standalone: true,
})
export class NumberInputComponent implements ControlValueAccessor, Validator {
  public onValueChange: Function;
  public onInputTouched: Function;

  public currentValue: number;
  public inputValue: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public label: any;
  @Input() public inputPattern = '^[0-9.,-]*$';
  @Input() public allowNegative = false;
  @Input() public placeholder: string;
  @Input() public id: string;
  public decimalSeparator: string;
  public thousandsSeparator: string;

  public readonly englishNumbersPattern = new RegExp(/^-?\d+(?:,\d{3})*(?:\.\d*)?$/);
  public readonly germanNumbersPattern = new RegExp(/^-?\d+(?:\.\d{3})*(?:,\d*)?$/);
  public readonly allowedKeys = ['Backspace'];
  // eslint-disable-next-line no-useless-escape
  public readonly suppressedKeys = new RegExp(/^[\sA-Za-z!"§$%&\/\(\)=\?;:_#'+~*\\ß<>|]$/);

  @ViewChild('inputElement', { static: true }) public inputElement: ElementRef;

  constructor() {
    this.decimalSeparator = (1.1).toLocaleString().slice(1, 2);
    this.thousandsSeparator = this.decimalSeparator === ',' ? '.' : ',';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public registerOnChange(fn: any): void {
    this.onValueChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public registerOnTouched(fn: any): void {
    this.onInputTouched = fn;
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    let ret = this.checkPatternAndConsecutiveDots();
    if (!ret) {
      ret = this.checkFormatAndNegative(control);
    }
    return ret;
  }

  private checkPatternAndConsecutiveDots(): ValidationErrors | null {
    if (!RegExp(this.inputPattern).exec(this.inputValue)) {
      return { invalidPattern: true };
    }
    // eslint-disable-next-line no-useless-escape
    if (RegExp(/[\.,]{2}/).exec(this.inputValue)) {
      return { consecutiveDotOrComma: true };
    }
    return null;
  }

  private checkFormatAndNegative(control: AbstractControl): ValidationErrors | null {
    const inputValue = control.value ? control.value.toString() : '';
    if (inputValue.length > 0) {
      const isGermanInput = inputValue.match(this.germanNumbersPattern);
      const isEnglishInput = inputValue.match(this.englishNumbersPattern);
      let commaCount = 0;
      let dotCount = 0;
      inputValue.split('').forEach((val: string) => {
        if (val === '.') {
          dotCount++;
        } else if (val === ',') {
          commaCount++;
        }
      });

      if ((!isGermanInput && !isEnglishInput) || (dotCount > 1 && commaCount > 1) || (!this.currentValue && this.inputValue)) {
        return { invalidFormat: true };
      }
      if (this.currentValue < 0 && this.allowNegative === false) {
        return { negativeNotAllowed: true };
      }
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public writeValue(obj: any): void {
    if (obj && !isNaN(obj)) {
      this.currentValue = Number(obj);

      // WTF: .toLocaleString() rounds the value to max 3 decimals
      this.inputValue = this.currentValue.toString().replace('.', ',');
    } else {
      this.currentValue = undefined;
      this.inputValue = '';
    }

    this.updateInputElementFromInputValue();
  }

  public isEmpty(): boolean {
    return typeof this.currentValue === 'undefined' && this.inputValue === '';
  }

  public inputChanged(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;

    const inputValue = target.value;

    this.inputValue = inputValue;
    if (!inputValue) {
      this.currentValue = undefined;
      this.onValueChange(this.currentValue);
      return;
    }

    const isGermanInput = RegExp(this.germanNumbersPattern).exec(inputValue);
    const isEnglishInput = RegExp(this.englishNumbersPattern).exec(inputValue);

    if (isGermanInput && isEnglishInput) {
      // this can happen if no decimals provided
      const numberFromGermanInput = Number(inputValue.replace(/\./g, '').replace(/,/g, '.'));
      const numberFromEnglishInput = Number(inputValue.replace(/,/g, ''));
      this.currentValue = numberFromGermanInput <= numberFromEnglishInput ? numberFromGermanInput : numberFromEnglishInput;
    } else if (isGermanInput) {
      this.currentValue = Number(inputValue.replace(/\./g, '').replace(/,/g, '.'));
    } else if (isEnglishInput) {
      this.currentValue = Number(inputValue.replace(/,/g, ''));
    } else {
      this.currentValue = undefined;
    }
    this.onValueChange(this.currentValue);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  public onFocus(_: Event): void {}
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onBlur(_: Event): void {
    this.onInputTouched();
  }

  public onKeyDown(event: KeyboardEvent): boolean {
    // increment / decrement with arrow keys
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      if (!this.currentValue) {
        this.currentValue = 0;
      }
      const newVal = this.currentValue + (event.key === 'ArrowUp' ? 1.0 : -1.0);

      // rounding is necessary: https://stackoverflow.com/questions/11832914/round-to-at-most-2-decimal-places-only-if-necessary
      this.currentValue = Math.round((newVal + Number.EPSILON) * 1000000) / 1000000;
      this.inputValue = this.currentValue.toString().replace('.', ',');
      this.updateInputElementFromInputValue();
      this.onValueChange(this.currentValue);
      event.stopPropagation();
      event.preventDefault();
      return false;
    }

    const enteredCommaOrDot = event.key === ',' || event.key === '.';
    const lastInputWasCommaOrDot = this.inputValue && (this.inputValue.endsWith(',') || this.inputValue.endsWith('.'));
    const specialKeyPressed = event.ctrlKey || event.metaKey || event.altKey;
    if (
      (event.key && !RegExp(this.inputPattern).exec(event.key) && RegExp(this.suppressedKeys).exec(event.key) && !specialKeyPressed) ||
      (enteredCommaOrDot && lastInputWasCommaOrDot)
    ) {
      event.stopPropagation();
      event.preventDefault();
      return false;
    }

    return true;
  }

  public updateInputElementFromInputValue(): void {
    this.inputElement.nativeElement.value = this.inputValue;
  }
}
